import Phaser from 'phaser';
import { PlayerPosition, PlayerCount, GAME_WIDTH, getGameConfig } from '@hokm/shared';
import { PERSIAN_THEME } from '../../../theme/persianTheme';
import { getPlayerColor } from '../utils/PlayerColors';

interface PlayerSlot {
  container: Phaser.GameObjects.Container;
  bg: Phaser.GameObjects.Rectangle;
  nameText: Phaser.GameObjects.Text;
  statusText: Phaser.GameObjects.Text;
  addAIButton: Phaser.GameObjects.Container;
  playerId?: string;
  isAI: boolean;
}

export interface PlayerSlotsCallbacks {
  onAddAI: (serverPosition: PlayerPosition) => void;
  toServerPosition: (displayPosition: PlayerPosition) => PlayerPosition;
}

/**
 * Manages the player slot display in diamond pattern.
 */
export class PlayerSlots {
  private slots: PlayerSlot[] = [];
  private currentPlayerCount: PlayerCount = 4;
  private isJoined: boolean = false;

  constructor(
    private scene: Phaser.Scene,
    private callbacks: PlayerSlotsCallbacks
  ) {
    this.buildSlots();
  }

  private buildSlots(): void {
    // Display positions in diamond pattern inside the form frame
    const positions = [
      { x: GAME_WIDTH / 2, y: 435, label: 'You' }, // South - bottom center
      { x: 510, y: 365, label: 'Left' }, // West - left side (inward)
      { x: GAME_WIDTH / 2, y: 290, label: 'Across' }, // North - below room code
      { x: 770, y: 365, label: 'Right' }, // East - right side (inward)
    ];

    for (let i = 0; i < 4; i++) {
      const pos = positions[i];
      const container = this.scene.add.container(pos.x, pos.y).setVisible(false);

      // Smaller, more compact player boxes
      const bg = this.scene.add
        .rectangle(0, 0, 90, 40, PERSIAN_THEME.colors.jade)
        .setStrokeStyle(1, PERSIAN_THEME.colors.bronze);

      const nameText = this.scene.add
        .text(0, -8, 'Empty', {
          fontFamily: 'Arial',
          fontSize: '13px',
          color: PERSIAN_THEME.hex.textSecondary,
        })
        .setOrigin(0.5);

      const statusText = this.scene.add
        .text(0, 7, pos.label, {
          fontFamily: 'Arial',
          fontSize: '9px',
          color: PERSIAN_THEME.hex.textSecondary,
        })
        .setOrigin(0.5);

      container.add([bg, nameText, statusText]);

      // Add AI button below the box
      const displayPos = i as PlayerPosition;
      const addAIBtn = this.createSmallButton(0, 25, '+AI', () => {
        const serverPos = this.callbacks.toServerPosition(displayPos);
        this.callbacks.onAddAI(serverPos);
      });
      addAIBtn.setVisible(false);
      container.add(addAIBtn);

      this.slots.push({
        container,
        bg,
        nameText,
        statusText,
        addAIButton: addAIBtn,
        isAI: false,
      });
    }
  }

  private createSmallButton(x: number, y: number, text: string, onClick: () => void): Phaser.GameObjects.Container {
    const container = this.scene.add.container(x, y);

    const bg = this.scene.add
      .rectangle(0, 0, 40, 20, PERSIAN_THEME.colors.buttonBg)
      .setStrokeStyle(1, PERSIAN_THEME.colors.bronze);

    const label = this.scene.add
      .text(0, 0, text, {
        fontFamily: 'Arial',
        fontSize: '10px',
        color: PERSIAN_THEME.hex.textPrimary,
      })
      .setOrigin(0.5);

    container.add([bg, label]);
    container.setSize(40, 20);
    container.setInteractive({ useHandCursor: true });

    container.on('pointerover', () => bg.setFillStyle(PERSIAN_THEME.colors.buttonHover));
    container.on('pointerout', () => bg.setFillStyle(PERSIAN_THEME.colors.buttonBg));
    container.on('pointerdown', onClick);

    return container;
  }

  setJoined(joined: boolean): void {
    this.isJoined = joined;
  }

  setPlayerCount(count: PlayerCount): void {
    this.currentPlayerCount = count;
  }

  showForPlayerCount(playerCount: PlayerCount): void {
    this.currentPlayerCount = playerCount;
    const config = getGameConfig(playerCount);

    this.slots.forEach((slot, index) => {
      const isActivePosition = (config.positions as readonly number[]).includes(index);
      slot.container.setVisible(isActivePosition);
      slot.addAIButton.setVisible(isActivePosition);
    });
  }

  updateSlot(position: PlayerPosition, name: string, isAI: boolean, playerId?: string): void {
    const slot = this.slots[position];
    slot.nameText.setText(name);
    slot.nameText.setColor(isAI ? '#ffaa00' : '#ffffff');
    slot.statusText.setColor('#aaaaaa');
    slot.playerId = playerId;
    slot.isAI = isAI;
    slot.addAIButton.setVisible(false);

    // Apply player color based on position and game mode
    const playerColor = getPlayerColor(position, this.currentPlayerCount);
    slot.bg.setStrokeStyle(2, playerColor);
  }

  clearSlot(position: PlayerPosition): void {
    const slot = this.slots[position];
    slot.nameText.setText('Empty');
    slot.nameText.setColor('#888888');
    slot.playerId = undefined;
    slot.isAI = false;
    if (this.isJoined) {
      slot.addAIButton.setVisible(true);
    }

    // Reset to default border
    slot.bg.setStrokeStyle(1, PERSIAN_THEME.colors.bronze);
  }

  getFilledSlotCount(): number {
    return this.slots.filter((s) => s.playerId !== undefined).length;
  }

  hideAll(): void {
    this.slots.forEach((slot) => {
      slot.container.setVisible(false);
      slot.addAIButton.setVisible(false);
    });
  }

  /**
   * Reset all slots to empty state and hide them.
   * Call this when leaving a room to clear stale data.
   */
  resetAll(): void {
    this.slots.forEach((slot) => {
      slot.nameText.setText('Empty');
      slot.nameText.setColor('#888888');
      slot.playerId = undefined;
      slot.isAI = false;
      slot.addAIButton.setVisible(false);
      slot.bg.setStrokeStyle(1, PERSIAN_THEME.colors.bronze);
      slot.container.setVisible(false);
    });
    this.isJoined = false;
  }

  destroy(): void {
    this.slots.forEach((slot) => slot.container.destroy());
    this.slots = [];
  }

  /**
   * Get containers for active slots only (based on current player count).
   * Used for crossfade transitions.
   */
  getContainers(): Phaser.GameObjects.Container[] {
    const config = getGameConfig(this.currentPlayerCount);
    return this.slots
      .filter((_, index) => (config.positions as readonly number[]).includes(index))
      .map((slot) => slot.container);
  }
}
